import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Download, GitCompareArrows, Loader2 } from "lucide-react";
import JurisdictionSelect, { TurkiyeNotice } from "../components/JurisdictionSelect";

const FOCUSES = ["implementation", "interpretation", "practice", "enforcement"];

export default function ComparativeLaw() {
    const [topic, setTopic] = useState("");
    const [type, setType] = useState("ms_vs_ms");
    const [focus, setFocus] = useState("implementation");
    const [instrument, setInstrument] = useState("");
    const [memberStates, setMemberStates] = useState([]);
    const [jurisdiction, setJurisdiction] = useState("eu");
    const [allStates, setAllStates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/comparative/member-states").then(({ data }) => setAllStates(data.items));
    }, []);

    const toggleState = (s) => setMemberStates((cur) =>
        cur.includes(s) ? cur.filter((x) => x !== s) : cur.length < 6 ? [...cur, s] : cur
    );

    const canSubmit =
        topic.trim().length >= 4 &&
        (jurisdiction !== "eu" ||
            (type === "ms_vs_ms" && memberStates.length >= 2) ||
            (type === "eu_vs_national" && memberStates.length >= 1));

    const submit = async (e) => {
        e?.preventDefault();
        if (!canSubmit) return;
        setError("");
        setLoading(true);
        try {
            const { data } = await api.post("/comparative/run", {
                topic,
                comparison_type: type,
                focus,
                eu_instrument: instrument || undefined,
                member_states: memberStates,
                jurisdiction,
            });
            const id = data.id;
            for (let i = 0; i < 90; i++) {
                await new Promise((r) => setTimeout(r, 2500));
                const { data: doc } = await api.get(`/comparative/runs/${id}`);
                if (doc.status === "ready") {
                    setResult(doc.result);
                    setLoading(false);
                    return;
                }
                if (doc.status === "failed") {
                    setError(doc.error || "Comparison failed.");
                    setLoading(false);
                    return;
                }
            }
            setError("Timed out waiting for the comparison. It may still complete in the background.");
        } catch (e2) {
            const d = e2?.response?.data?.detail;
            setError(typeof d === "string" ? d : d?.message || "Comparison failed.");
        } finally {
            setLoading(false);
        }
    };

    const exportCSV = () => {
        if (!result) return;
        const rows = [["Dimension", ...(result.columns || [])]];
        for (const r of result.rows || []) {
            rows.push([r.dimension, ...(r.cells || [])]);
        }
        const csv = rows.map((row) =>
            row.map((c) => `"${String(c || "").replace(/"/g, '""')}"`).join(",")
        ).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `comparative-${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="container-edit py-10 lg:py-14 max-w-6xl" data-testid="comparative-law">
            <div className="label-eyebrow">Module</div>
            <h1 className="font-display text-3xl lg:text-5xl mt-2 tracking-tight">Comparative Law</h1>
            <p className="mt-3 text-[#0D1B3E]/75 max-w-2xl">
                Cross-Member-State or EU-vs-national comparison with structured table,
                synthesis and CSV export. 3 credits per run.
            </p>

            <form onSubmit={submit} className="mt-8 card-flat p-6 space-y-4" data-testid="comparative-form">
                <div className="flex flex-wrap items-center gap-3">
                    <JurisdictionSelect
                        value={jurisdiction}
                        onChange={setJurisdiction}
                        testId="comparative-jurisdiction"
                    />
                </div>
                <TurkiyeNotice jurisdiction={jurisdiction} className="max-w-2xl" />
                <Field label="Topic" value={topic} onChange={setTopic}
                       placeholder="e.g. Lawful basis for processing biometric data in the workplace"
                       testId="comparative-input-topic" />
                <div className="grid gap-4 sm:grid-cols-3">
                    <Select label="Comparison" value={type} onChange={setType} testId="comparative-input-type"
                            options={[{value:"ms_vs_ms",label:"Member State vs Member State"},{value:"eu_vs_national",label:"EU vs national"}]} />
                    <Select label="Focus" value={focus} onChange={setFocus} testId="comparative-input-focus"
                            options={FOCUSES.map(f => ({value:f,label:f}))} />
                    <Field label="EU instrument (anchor, optional)" value={instrument} onChange={setInstrument}
                           placeholder="e.g. GDPR" testId="comparative-input-instrument" />
                </div>
                <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/70 mb-2">
                        Member States{" "}
                        {jurisdiction !== "eu"
                            ? "(optional, up to 6)"
                            : type === "ms_vs_ms" ? "(pick 2-6)" : "(pick 1-6)"}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {allStates.map((s) => (
                            <button key={s} type="button" onClick={() => toggleState(s)}
                                    data-testid={`comparative-ms-${s}`}
                                    className={`px-2.5 py-1 text-xs border ${
                                        memberStates.includes(s)
                                            ? "bg-[#0D1B3E] text-[#F5F0E8] border-[#0D1B3E]"
                                            : "border-[#0D1B3E]/20 hover:bg-[#0D1B3E]/5"
                                    }`}>
                                {s}
                            </button>
                        ))}
                    </div>
                </div>
                {error && <p className="text-sm text-red-700" data-testid="comparative-error">{error}</p>}
                <div className="flex justify-end">
                    <button type="submit" disabled={!canSubmit || loading}
                            className="btn-primary disabled:opacity-50" data-testid="comparative-submit">
                        {loading ? <Loader2 size={14} className="animate-spin" /> : <GitCompareArrows size={14} />}
                        Run comparison
                    </button>
                </div>
            </form>

            {result && (
                <section className="mt-10" data-testid="comparative-result">
                    <div className="flex items-end justify-between gap-3 flex-wrap">
                        <p className="font-display text-2xl tracking-tight leading-snug max-w-3xl">
                            {result.headline}
                        </p>
                        <button type="button" onClick={exportCSV} className="btn-secondary"
                                data-testid="comparative-export">
                            <Download size={14} /> Export CSV
                        </button>
                    </div>

                    <div className="mt-6 overflow-x-auto">
                        <table className="w-full border-collapse text-sm" data-testid="comparative-table">
                            <thead>
                                <tr>
                                    <th className="border-b border-[#0D1B3E]/15 py-2 px-3 text-left text-[10px] uppercase tracking-[0.22em] text-[#0D1B3E]/55">
                                        Dimension
                                    </th>
                                    {(result.columns || []).map((c) => (
                                        <th key={c} className="border-b border-[#0D1B3E]/15 py-2 px-3 text-left text-[10px] uppercase tracking-[0.22em] text-[#0D1B3E]/55">
                                            {c}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {(result.rows || []).map((r, i) => (
                                    <tr key={i} className="align-top">
                                        <td className="border-b border-[#0D1B3E]/10 py-3 px-3 font-display">{r.dimension}</td>
                                        {(r.cells || []).map((c, j) => (
                                            <td key={j} className="border-b border-[#0D1B3E]/10 py-3 px-3">{c}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {result.synthesis?.length > 0 && (
                        <div className="mt-7">
                            <div className="label-eyebrow">Synthesis</div>
                            <ul className="mt-2 list-disc pl-5 text-sm space-y-1" data-testid="comparative-synthesis">
                                {result.synthesis.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                        </div>
                    )}

                    {result.primary_sources?.length > 0 && (
                        <div className="mt-7">
                            <div className="label-eyebrow">Primary sources</div>
                            <ul className="mt-2 text-sm" data-testid="comparative-sources">
                                {result.primary_sources.map((s, i) => (
                                    <li key={i}>
                                        <span className="text-[10px] uppercase tracking-[0.18em] text-[#C9A84C] mr-2">
                                            {s.jurisdiction}
                                        </span>
                                        {s.label}
                                        {s.url && (
                                            <a href={s.url} target="_blank" rel="noreferrer"
                                               className="ml-2 text-xs text-[#0D1B3E]/70 underline hover:text-[#C9A84C]"
                                               data-testid={`comparative-source-link-${i}`}>
                                                Verify in official source
                                            </a>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {result.caveats?.length > 0 && (
                        <div className="mt-7">
                            <div className="label-eyebrow">Caveats</div>
                            <ul className="mt-2 list-disc pl-5 text-sm text-[#0D1B3E]/70">
                                {result.caveats.map((c, i) => <li key={i}>{c}</li>)}
                            </ul>
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}

function Field({ label, value, onChange, placeholder, testId }) {
    return (
        <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/70 mb-2">{label}</span>
            <input type="text" className="field" value={value} placeholder={placeholder}
                   onChange={(e) => onChange(e.target.value)} data-testid={testId} />
        </label>
    );
}

function Select({ label, value, onChange, options, testId }) {
    return (
        <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/70 mb-2">{label}</span>
            <select className="field" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </label>
    );
}
